import type { MouseEvent } from "react";

// Unified switch primitive. Two modes:
//
//   <Switch checked={x} onChange={f} ariaLabel="…" />
//     bare track — for in-row boolean settings (notifications, debug
//     toggles, Match's Agentic AI master switch).
//
//   <Switch checked={x} onChange={f} ariaLabel="…"
//           label={{ on: "Teilen", off: "Privat" }} />
//     labeled pill — for sharing-style toggles where the affordance
//     carries its own text (Profile entries, AddForm forms). Visually
//     identical to the former <ShareSwitch>. Click bubbling is stopped
//     automatically because labeled switches typically live inside
//     click-to-expand rows.
//
// `exiting` is only meaningful with `label` — it swaps the mount-in
// animation for a fade-out. The caller keeps the element mounted for
// ~200ms so the animation can finish.

interface SwitchLabel {
  on: string;
  off: string;
}

interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
  label?: SwitchLabel;
  disabled?: boolean;
  exiting?: boolean;
  stopPropagation?: boolean;
  className?: string;
  title?: string;
  // Optional DOM id set on the <button> — a stable, locale-independent hook
  // for e2e tests (spark-validation), mirroring Select's `id`.
  id?: string;
}

export function Switch({
  checked,
  onChange,
  ariaLabel,
  label,
  disabled = false,
  exiting = false,
  stopPropagation = false,
  className = "",
  title,
  id,
}: SwitchProps) {
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation || label) e.stopPropagation();
    onChange(!checked);
  };

  if (label) {
    return (
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        disabled={disabled}
        title={title}
        onClick={handleClick}
        className={`${exiting ? "toggle-pop-out" : "toggle-pop"} inline-flex items-center gap-1.5 rounded-full px-1.5 py-0.5 ring-1 transition-colors duration-200 ease-in-out disabled:opacity-60 ${
          checked
            ? "bg-accent/10 text-accent ring-accent/30"
            : "bg-surface text-ink/40 ring-ink/10 hover:text-ink/55"
        } ${className}`}
      >
        <span className="w-10 text-center text-[8px] font-semibold tracking-[0.16em]">
          {checked ? label.on : label.off}
        </span>
        <span
          className={`relative h-3.5 w-6 rounded-full transition-colors duration-200 ease-in-out ${
            checked ? "bg-accent/70" : "bg-ink/10"
          }`}
        >
          <span
            className={`absolute top-0.5 h-2.5 w-2.5 rounded-full bg-surface shadow-sm transition-all duration-200 ease-in-out ${
              checked ? "left-3" : "left-0.5"
            }`}
          />
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      title={title}
      onClick={handleClick}
      className={`relative h-6 w-10 shrink-0 rounded-full transition-colors disabled:opacity-60 ${
        checked ? "bg-accent" : "bg-ink/20"
      } ${className}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-page shadow transition-transform ${
          checked ? "translate-x-4" : ""
        }`}
      />
    </button>
  );
}
